import React from "react";
import { Box, Tooltip } from "@mui/material";

/** Shared inventory alert statuses for medicine / pathology tables. */
export const INVENTORY_STATUS = {
  EXPIRED: "expired",
  EXPIRING_SOON: "nearing",
  OUT_OF_STOCK: "out_of_stock",
  LOW_STOCK: "low_stock",
  GOOD: "good",
};

export const INVENTORY_STATUS_META = {
  expired: {
    key: "expired",
    label: "Expired",
    tooltip: "Expired stock",
    rowBg: "#f3e5f5",
    chipSx: { bgcolor: "#6a1b9a", color: "#fff" },
  },
  nearing: {
    key: "nearing",
    label: "Expiring soon",
    tooltip: "Stock expiring soon",
    rowBg: "#e1f5fe",
    chipSx: { bgcolor: "#0277bd", color: "#fff" },
  },
  out_of_stock: {
    key: "out_of_stock",
    label: "Out of stock",
    tooltip: "No usable stock available",
    // Match Low Stock Alert popup (error)
    rowBg: "#ffebee",
    chipSx: { bgcolor: "#d32f2f", color: "#fff" },
  },
  low_stock: {
    key: "low_stock",
    label: "Low stock",
    tooltip: "Quantity at or below low-stock threshold",
    // Match Low Stock Alert popup (warning)
    rowBg: "#fff3e0",
    chipSx: { bgcolor: "#ed6c02", color: "#fff" },
  },
  good: {
    key: "good",
    label: "Good",
    tooltip: "Stock within acceptable range",
    rowBg: "#e8f5e9",
    chipSx: { bgcolor: "#2e7d32", color: "#fff" },
  },
};

/** Legend order shown above inventory tables. */
export const INVENTORY_STATUS_LEGEND_KEYS = [
  "expired",
  "nearing",
  "out_of_stock",
  "low_stock",
];

export const getStockStatus = (quantity, threshold) => {
  const qty = Number(quantity);
  const safeQty = Number.isFinite(qty) ? qty : 0;
  const limit = Number(threshold);
  const safeLimit = Number.isFinite(limit) && limit >= 0 ? limit : 10;
  if (safeQty <= 0) return INVENTORY_STATUS.OUT_OF_STOCK;
  if (safeQty <= safeLimit) return INVENTORY_STATUS.LOW_STOCK;
  return null;
};

export const getInventoryRowBackground = ({
  expiryStatus = null,
  stockStatus = null,
} = {}) => {
  if (expiryStatus === INVENTORY_STATUS.EXPIRED) {
    return INVENTORY_STATUS_META.expired.rowBg;
  }
  if (stockStatus === INVENTORY_STATUS.OUT_OF_STOCK) {
    return INVENTORY_STATUS_META.out_of_stock.rowBg;
  }
  if (expiryStatus === INVENTORY_STATUS.EXPIRING_SOON) {
    return INVENTORY_STATUS_META.nearing.rowBg;
  }
  if (stockStatus === INVENTORY_STATUS.LOW_STOCK) {
    return INVENTORY_STATUS_META.low_stock.rowBg;
  }
  if (expiryStatus === INVENTORY_STATUS.GOOD) {
    return INVENTORY_STATUS_META.good.rowBg;
  }
  return "grey.50";
};

export const getInventoryNameColor = ({
  expiryStatus = null,
  stockStatus = null,
} = {}) => {
  if (expiryStatus === INVENTORY_STATUS.EXPIRED) return "#6a1b9a";
  if (stockStatus === INVENTORY_STATUS.OUT_OF_STOCK) return "error.main";
  if (expiryStatus === INVENTORY_STATUS.EXPIRING_SOON) return "#0277bd";
  if (stockStatus === INVENTORY_STATUS.LOW_STOCK) return "warning.main";
  if (expiryStatus === INVENTORY_STATUS.GOOD) return "success.main";
  return "inherit";
};

/**
 * Collect badge keys for a grouped inventory row.
 * Expiry badges reflect batch flags; stock badges use total usable quantity.
 */
export const getGroupInventoryBadgeKeys = (group, stockThreshold) => {
  const keys = [];
  if (group?.hasExpiredStock) keys.push(INVENTORY_STATUS.EXPIRED);
  if (!group?.hasExpiredStock && group?.hasNearingExpiry) {
    keys.push(INVENTORY_STATUS.EXPIRING_SOON);
  }
  const stockStatus = getStockStatus(group?.totalQuantity, stockThreshold);
  if (stockStatus) keys.push(stockStatus);
  return keys;
};

export const InventoryStatusChip = ({ statusKey, sx = {} }) => {
  const meta = INVENTORY_STATUS_META[statusKey];
  if (!meta) return null;
  return (
    <Tooltip title={meta.tooltip}>
      <Box
        component="span"
        sx={{
          display: "inline-block",
          width: 10,
          height: 10,
          borderRadius: "50%",
          bgcolor: meta.chipSx.bgcolor,
          flexShrink: 0,
          verticalAlign: "middle",
          ...sx,
        }}
      />
    </Tooltip>
  );
};

export const InventoryStatusBadges = ({ statusKeys = [], sx = {} }) => {
  if (!statusKeys?.length) return null;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 0.5,
        ml: 0.75,
        verticalAlign: "middle",
        ...sx,
      }}
    >
      {statusKeys.map((key) => (
        <InventoryStatusChip key={key} statusKey={key} />
      ))}
    </Box>
  );
};

/** Compact color legend for inventory tables. */
export const InventoryStatusLegend = ({ sx = {} }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 0.75,
      px: 0.5,
      py: 0.25,
      ...sx,
    }}
  >
    {INVENTORY_STATUS_LEGEND_KEYS.map((key) => {
      const meta = INVENTORY_STATUS_META[key];
      return (
        <Box
          key={key}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: meta.chipSx.bgcolor,
              flexShrink: 0,
            }}
          />
          <Box
            component="span"
            sx={{ fontSize: "0.7rem", color: "text.secondary", lineHeight: 1.2 }}
          >
            {meta.label}
          </Box>
        </Box>
      );
    })}
  </Box>
);
